import React, { useMemo } from 'react';
import {
  isMetaAnswerKey,
  isPngFileName,
  normalizeFileAnswerValueForKey,
  prettyAnswerKey,
  uploadedFilesFromAnswers,
} from '../../../features/flow/utils';

function isLikelyUploadKey(key: string): boolean {
  const k = key.toLowerCase();
  // Covers keys like `employer_1_upload_statement_rsu` and similar upload/file payload fields.
  return k.includes('upload') || k.includes('upload_') || k.includes('file');
}

function isIncomeReviewKey(key: string): boolean {
  return (
    /^employer_\d+_/.test(key) ||
    key === 'employment_country' ||
    key.includes('employment_income') ||
    key.includes('wages_salary') ||
    key === 'had_freelance_income' ||
    /^business_\d+_/.test(key) ||
    /^self_employment_/.test(key) ||
    /^expense_/.test(key) ||
    /^vehicle_/.test(key) ||
    /^asset_\d+_/.test(key) ||
    /^interest_\d+_/.test(key) ||
    /^dividend_\d+_/.test(key) ||
    /^disposal_\d+_/.test(key) ||
    /^investment_\d+_/.test(key) ||
    key === 'investment_income_types' ||
    key.includes('capital_gain') ||
    key === 'have_rental_properties' ||
    key === 'own_other_properties' ||
    key === 'sold_any_property_2024' ||
    /^rental_\d+_/.test(key) ||
    /^property_\d+_/.test(key) ||
    /^sale_\d+_/.test(key) ||
    key === 'other_income_types' ||
    /^other_income/.test(key) ||
    key.includes('unemployment') ||
    key.includes('pension') ||
    key.includes('social_security')
  );
}

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function isDateLikeKey(key: string) {
  return /(date|dob|birth)/i.test(key);
}

function isIsoDateString(v: unknown) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
}

type Row = { key: string; label: string };

function uploadedOnScreenLabel(answerKey: string): string {
  if (/^employer_\d+_/.test(answerKey)) return 'Employment income details';
  if (/^business_\d+_/.test(answerKey)) return 'Business basics';
  if (/^self_employment_/.test(answerKey)) return 'Self-employment social taxes';
  if (/^expense_/.test(answerKey) || /^asset_\d+_/.test(answerKey) || /^vehicle_/.test(answerKey)) {
    return 'Business expenses per business';
  }
  if (/^interest_\d+_|^dividend_\d+_/.test(answerKey)) return 'Interest & dividends';
  if (/^disposal_\d+_/.test(answerKey)) return 'Disposals';
  if (/^rental_\d+_/.test(answerKey)) return 'Rental properties';
  if (/^property_\d+_/.test(answerKey)) return 'Other owned properties';
  if (/^sale_\d+_/.test(answerKey)) return 'Sale of properties not main home';
  if (/^other_income/.test(answerKey)) return 'Other income for each selected type';
  return prettyAnswerKey(answerKey);
}

function fileGroupLabel(answerKey: string): string {
  const dep = answerKey.match(/^dependent_(\d+)_(.+)$/);
  if (dep) {
    const idx = dep[1];
    const rest = dep[2];
    return `Dependent ${idx} ${prettyAnswerKey(rest)}`;
  }
  const emp = answerKey.match(/^employer_(\d+)_(.+)$/);
  if (emp) {
    const idx = emp[1];
    const rest = emp[2];
    return `Employer ${idx} ${prettyAnswerKey(rest)}`;
  }
  const biz = answerKey.match(/^business_(\d+)_(.+)$/);
  if (biz) {
    const idx = biz[1];
    const rest = biz[2];
    return `Business ${idx} ${prettyAnswerKey(rest)}`;
  }
  const inv = answerKey.match(/^(interest|dividend|disposal)_(\d+)_(.+)$/);
  if (inv) {
    const kind = prettyAnswerKey(inv[1]);
    const idx = inv[2];
    const rest = inv[3];
    return `${kind} ${idx} ${prettyAnswerKey(rest)}`;
  }
  const investment = answerKey.match(/^investment_(\d+)_(.+)$/);
  if (investment) {
    const idx = investment[1];
    const rest = investment[2];
    return `Investment ${idx} ${prettyAnswerKey(rest)}`;
  }
  return prettyAnswerKey(answerKey);
}

export function IncomeReviewScreenBody({
  answers,
  actionBusy,
  setAnswers,
}: {
  answers: Record<string, unknown>;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const sections = useMemo(() => {
    const keys = Object.keys(answers ?? {}).filter((k) => !isMetaAnswerKey(k) && isIncomeReviewKey(k));
    const push = (arr: Row[], key: string) => {
      if (isLikelyUploadKey(key)) return;
      const v = answers[key];
      if (isEmptyValue(v)) return;
      arr.push({ key, label: prettyAnswerKey(key) });
    };
    const section = (title: string, rows: Row[]) => ({ title, rows });

    const employment: Row[] = [];
    keys
      .filter((k) => /^employer_\d+_/.test(k) || k === 'employment_country' || k.includes('employment_income') || k.includes('wages_salary'))
      .sort()
      .forEach((k) => push(employment, k));

    const business: Row[] = [];
    keys
      .filter((k) => k === 'had_freelance_income' || /^business_\d+_/.test(k) || /^self_employment_/.test(k) || /^expense_/.test(k) || /^vehicle_/.test(k) || /^asset_\d+_/.test(k))
      .sort()
      .forEach((k) => push(business, k));

    const investments: Row[] = [];
    keys
      .filter((k) => /^interest_\d+_/.test(k) || /^dividend_\d+_/.test(k) || /^disposal_\d+_/.test(k) || k === 'investment_income_types' || k.includes('capital_gain'))
      .sort()
      .forEach((k) => push(investments, k));

    const properties: Row[] = [];
    keys
      .filter((k) => k === 'have_rental_properties' || k === 'own_other_properties' || k === 'sold_any_property_2024' || /^rental_\d+_/.test(k) || /^property_\d+_/.test(k) || /^sale_\d+_/.test(k))
      .sort()
      .forEach((k) => push(properties, k));

    const otherIncome: Row[] = [];
    keys
      .filter((k) => k === 'other_income_types' || /^other_income/.test(k) || k.includes('unemployment') || k.includes('pension') || k.includes('social_security'))
      .sort()
      .forEach((k) => push(otherIncome, k));

    return [
      section('Employment Review', employment),
      section('Self-Employment / Business', business),
      section('Investments', investments),
      section('Property & Rentals', properties),
      section('Other Income Types', otherIncome),
    ].filter((s) => s.rows.length > 0);
  }, [answers]);

  const uploadedFilesForReview = useMemo(
    () =>
      uploadedFilesFromAnswers(answers)
        .filter((row) => isIncomeReviewKey(row.answerKey))
        .map((row) => ({
          ...row,
          screenName:
            uploadedOnScreenLabel(row.answerKey),
          groupLabel: fileGroupLabel(row.answerKey),
          sectionName:
            /^employer_\d+_/.test(row.answerKey)
              ? 'Employment Review'
              : /^business_\d+_|^self_employment_|^expense_|^asset_\d+_|^vehicle_/.test(row.answerKey)
                ? 'Self-Employment / Business'
                : /^interest_\d+_|^dividend_\d+_|^disposal_\d+_|investment_income_types/.test(row.answerKey)
                  ? 'Investments'
                  : /^rental_\d+_|^property_\d+_|^sale_\d+_/.test(row.answerKey)
                    ? 'Property & Rentals'
                    : /^other_income/.test(row.answerKey)
                      ? 'Other Income Types'
                      : row.sectionName === 'Other'
                        ? 'Income'
                        : row.sectionName,
        }))
        .sort((a, b) => {
          const s = a.sectionName.localeCompare(b.sectionName);
          if (s !== 0) return s;
          const k = a.answerKey.localeCompare(b.answerKey);
          if (k !== 0) return k;
          return a.fileIndex - b.fileIndex;
        }),
    [answers]
  );

  return (
    <div className="w-full flex flex-col gap-10">
      {sections.map((sec, idx) => (
        <div key={sec.title} className="w-full">
          {idx > 0 ? <div className="w-full border-t border-[#E2E8F0] pt-10" /> : null}
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-base font-medium text-[#131315]">{sec.title}:</div>
          </div>

          <div className="mt-6 w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
            {sec.rows.map(({ label, key }) => {
              const v = answers[key];
              const valStr = v === null || v === undefined ? '' : String(v);
              const isNum = typeof v === 'number';
              const isArr = Array.isArray(v);
              const isDate = isIsoDateString(v) || isDateLikeKey(key);

              return (
                <div key={key} className="min-w-0 col-span-1">
                  <div className="flex flex-col gap-3">
                    <label className="block text-sm text-[#131315] font-medium">{label}</label>
                    {isArr ? (
                      <textarea
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        value={JSON.stringify(v)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          try {
                            const parsed = JSON.parse(raw);
                            setAnswers((prev) => ({ ...prev, [key]: parsed }));
                          } catch {
                            setAnswers((prev) => ({ ...prev, [key]: raw }));
                          }
                        }}
                        rows={3}
                        disabled={actionBusy}
                      />
                    ) : (
                      <input
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        type={isDate ? 'date' : isNum ? 'number' : 'text'}
                        value={valStr}
                        onChange={(e) =>
                          setAnswers((prev) => ({
                            ...prev,
                            [key]: isNum ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          }))
                        }
                        disabled={actionBusy}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {uploadedFilesForReview.length > 0 ? (
        <div className="w-full border-t border-[#E2E8F0] pt-10">
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-sm font-semibold text-[#131315]">Uploaded Files:</div>
          </div>

          <div className="mt-4 w-full max-w-[668px] flex flex-col gap-3">
            {(() => {
              const seen = new Map<string, number>();
              return uploadedFilesForReview.map((row) => {
                const next = (seen.get(row.groupLabel) ?? 0) + 1;
                seen.set(row.groupLabel, next);
                return (
                  <div key={`${row.answerKey}_${row.fileName}_${row.fileIndex}`}>
                    <div className="text-xs text-[#525353] mb-2">{row.groupLabel}</div>
                    <div className="w-full max-w-[310px] rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] px-4 py-3 flex items-center justify-between">
                      <span className="text-sm text-[#131315] break-all">
                        <span className="block text-base font-medium text-[#131315] mb-1">File {next}: {row.fileName}</span>
                      </span>
                      <button
                        type="button"
                        disabled={actionBusy}
                        onClick={() => {
                          setAnswers((prev) => {
                            const curVal = prev[row.answerKey];
                            const curAll = normalizeFileAnswerValueForKey(row.answerKey, curVal);
                            let pngSeen = -1;
                            const nextAll = curAll.filter((ref) => {
                              if (!isPngFileName(ref.name)) return true;
                              pngSeen += 1;
                              if (pngSeen === row.fileIndex && ref.name === row.fileName) return false;
                              return true;
                            });
                            return { ...prev, [row.answerKey]: nextAll.length > 0 ? nextAll : null };
                          });
                        }}
                        className="text-base font-medium text-[#131315] disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
